import { Component, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary — catches render/lifecycle errors in the React subtree.
 *
 * Without this, any unhandled error in a child component unmounts the
 * entire React tree, leaving a blank screen with no way to recover.
 *
 * Errors are automatically reported to Sentry (if configured).
 *
 * Usage:
 *   <ErrorBoundary fallback={<ErrorFallback />}>
 *     <Suspense fallback={<Loader />}>
 *       <SomePage />
 *     </Suspense>
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack)

    // Report to Sentry with component stack context
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: info.componentStack,
        },
      },
    })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-4 p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10 text-red-500">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-surface-900 dark:text-white">
              Algo salió mal al cargar esta sección
            </p>
            <p className="text-xs text-surface-400 font-mono">
              {this.state.error?.message || 'Error desconocido'}
            </p>
          </div>
          <button
            onClick={this.handleReset}
            className="rounded-lg bg-[#00ab81] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#00926f]"
          >
            Reintentar
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
